import React, { useEffect, useState } from "react";
import { Prompt } from "react-router-dom";
import { useLazyQuery, useMutation, useQuery } from "@apollo/react-hooks";
import { gql } from "apollo-boost";
import moment from "moment";
import Files from "react-butterfiles";

import CircularProgress from "@material-ui/core/CircularProgress";
import Switch from "@material-ui/core/Switch";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import StorefrontIcon from "@material-ui/icons/Storefront";

import { graphqlUrl, endPoint } from "utils/config";
import { login } from "utils/auth";
import Button from "designV2/components/Button";
import Field from "designV2/components/Field";
import Panel from "designV2/components/Panel";
import PageHeader from "designV2/components/PageHeader";
import SearchField from "designV2/components/SearchField";
import { Skeleton } from "designV2/components/Skeleton";
import FunMap from "./FunMap";
import "./addShop.css";

/* ------------------------------------------------------------------
   GraphQL
   ------------------------------------------------------------------
   Creating only. The update and resubmit-after-rejection mutations
   that used to live here are owned by shopAdminViews/editShop, which
   is where both of those journeys actually go — they were dead in this
   file, reachable only through a `shop` prop no caller passes.
   ------------------------------------------------------------------ */

const CREATE_SHOP_BY_SHOP_ADMIN = gql`
  mutation createShopByShopAdmin(
    $budget: Budget
    $active: Boolean
    $rootCategory: ID
    $name: String
    $preparingTime: Int!
    $minimumPurchase: Int!
    $newOrderSMSNotificationPhoneNumber: String
    $newOrderSMSNotificationEnabled: Boolean!
    $address: String
    $phoneNumbers: [String!]!
    $location: ShopLocationInput!
    $origin: String
    $categories: [ID]
    $description: String
    $workingHoursInMinutes: [workingHoursInMinutesInput]
    $notWorkingDays: [notWorkingDaysInput]
    $bannerUrl: String
    $logoUrl: String
  ) {
    createShopByShopAdmin(
      shopData: {
        budget: $budget
        active: $active
        name: $name
        preparingTime: $preparingTime
        minimumPurchase: $minimumPurchase
        newOrderSMSNotificationPhoneNumber: $newOrderSMSNotificationPhoneNumber
        newOrderSMSNotificationEnabled: $newOrderSMSNotificationEnabled
        categories: $categories
        rootCategory: $rootCategory
        address: $address
        phoneNumbers: $phoneNumbers
        location: $location
        origin: $origin
        description: $description
        workingHoursInMinutes: $workingHoursInMinutes
        notWorkingDays: $notWorkingDays
        bannerUrl: $bannerUrl
        logoUrl: $logoUrl
      }
    ) {
      _id
      state
      name
      active
      rootCategory {
        _id
        title
      }
    }
  }
`;

const CREATE_SHOP_BY_ADMIN = gql`
  mutation createShopByAdmin(
    $shopAdminId: ID!
    $budget: Budget
    $active: Boolean
    $rootCategory: ID
    $name: String
    $preparingTime: Int!
    $minimumPurchase: Int!
    $newOrderSMSNotificationPhoneNumber: String
    $newOrderSMSNotificationEnabled: Boolean!
    $address: String
    $phoneNumbers: [String!]!
    $location: ShopLocationInput!
    $origin: String
    $categories: [ID]
    $description: String
    $workingHoursInMinutes: [workingHoursInMinutesInput]
    $notWorkingDays: [notWorkingDaysInput]
    $bannerUrl: String
    $logoUrl: String
  ) {
    createShopByAdmin(
      shopAdminId: $shopAdminId
      input: {
        budget: $budget
        active: $active
        name: $name
        preparingTime: $preparingTime
        minimumPurchase: $minimumPurchase
        newOrderSMSNotificationPhoneNumber: $newOrderSMSNotificationPhoneNumber
        newOrderSMSNotificationEnabled: $newOrderSMSNotificationEnabled
        rootCategory: $rootCategory
        address: $address
        categories: $categories
        phoneNumbers: $phoneNumbers
        location: $location
        origin: $origin
        description: $description
        workingHoursInMinutes: $workingHoursInMinutes
        notWorkingDays: $notWorkingDays
        bannerUrl: $bannerUrl
        logoUrl: $logoUrl
      }
    ) {
      _id
      state
      name
    }
  }
`;

const GET_TOKEN = gql`
  query getNewAccessTokenByAdmin($refreshToken: String!) {
    getNewAccessTokenByAdmin(refreshToken: $refreshToken) {
      token {
        refreshToken
        accessToken
      }
    }
  }
`;

const UPLOAD_FILE = gql`
  mutation uploadFile($file: Upload!, $folderName: String!) {
    uploadFile(data: { file: $file, folderName: $folderName }) {
      url
    }
  }
`;

const GET_CATEGORIES = gql`
  query getCategories($parent: ID) {
    getCategories(filters: { parent: $parent }) {
      _id
      title
    }
  }
`;

const GET_SHOP_ADMINS = gql`
  query getAdminsByAdmin($type: String, $fullName: String) {
    getAdminsByAdmin(filters: { type: $type, fullName: $fullName }) {
      _id
      fullName
      phoneNumber
      shop {
        _id
      }
    }
  }
`;

/* ---------------- constants ---------------- */

const BUDGET_OPTIONS = [
  { label: "$", value: "B", hint: "Inexpensive" },
  { label: "$$", value: "BB", hint: "Mid-range" },
  { label: "$$$", value: "BBB", hint: "Expensive" },
];

const WEEK_DAYS = [
  { value: "SAT", label: "Saturday" },
  { value: "SUN", label: "Sunday" },
  { value: "MON", label: "Monday" },
  { value: "TUE", label: "Tuesday" },
  { value: "WEN", label: "Wednesday" },
  { value: "THU", label: "Thursday" },
  { value: "FRI", label: "Friday" },
];

// Toronto. Only a starting point for the map — the browser's own
// location replaces it if the person allows that.
const DEFAULT_POSITION = { lat: 43.6532, lng: -79.3832 };

const DEFAULT_HOURS = { from: "09:00", to: "17:00" };

const timeToMinutes = (time) =>
  moment(time, "HH:mm").diff(moment(time, "HH:mm").startOf("day"), "minutes");

const readableError = (error) => {
  if (error && error.graphQLErrors && error.graphQLErrors.length) {
    return error.graphQLErrors[0].message;
  }
  if (error && error.networkError) {
    return "We could not reach the server. Check your connection and try again.";
  }
  return "Something went wrong. Please try again.";
};

/**
 * AddShop - creates a shop.
 *
 * Two callers, one form: a shop owner creating their own during
 * sign-up (`type="shopAdmin"`), and a super admin creating one for
 * somebody else (`type="superAdmin"`, which adds the owner picker and
 * sends a different mutation).
 *
 * The super admin gets the full page; the sign-up flow renders bare
 * inside the standalone page its wrapper provides.
 */
const AddShop = ({ type }) => {
  const isSuperAdmin = type === "superAdmin";

  const [shopAdminId, setShopAdminId] = useState("");
  const [adminSearch, setAdminSearch] = useState("");

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [budget, setBudget] = useState(BUDGET_OPTIONS[0].value);
  const [bannerImage, setBannerImage] = useState("");
  const [logoImage, setLogoImage] = useState("");
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const [rootCategoryId, setRootCategoryId] = useState("");
  const [rootCategoryTitle, setRootCategoryTitle] = useState("");
  const [categoryIds, setCategoryIds] = useState([]);

  const [phoneNumbers, setPhoneNumbers] = useState(["", "", ""]);
  const [streetAddress, setStreetAddress] = useState("");
  const [town, setTown] = useState("");
  const [province, setProvince] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [markerPosition, setMarkerPosition] = useState(DEFAULT_POSITION);

  const [workingHours, setWorkingHours] = useState({});

  const [preparingTime, setPreparingTime] = useState("");
  const [minimumPurchase, setMinimumPurchase] = useState("0");
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [smsPhone, setSmsPhone] = useState("");

  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const { loading: rootLoading, data: rootData } = useQuery(GET_CATEGORIES, {
    variables: { parent: null },
  });
  const { data: subData } = useQuery(GET_CATEGORIES, {
    variables: { parent: rootCategoryId || undefined },
  });

  const [getShopAdmins, { loading: adminsLoading, data: adminsData }] = useLazyQuery(
    GET_SHOP_ADMINS
  );

  const [uploadFile] = useMutation(UPLOAD_FILE);
  const [createShopByShopAdmin] = useMutation(CREATE_SHOP_BY_SHOP_ADMIN);
  const [createShopByAdmin] = useMutation(CREATE_SHOP_BY_ADMIN);
  const [getNewAccessToken, { data: tokenData }] = useLazyQuery(GET_TOKEN);

  const rootCategories = (rootData && rootData.getCategories) || [];
  const subCategories = (subData && subData.getCategories) || [];

  // Only admins without a shop can be given one.
  const shopAdmins = ((adminsData && adminsData.getAdminsByAdmin) || []).filter(
    (admin) => !admin.shop
  );
  const selectedAdmin = shopAdmins.find((admin) => admin._id === shopAdminId);

  const categoryLabel = rootCategoryTitle === "Restaurant" ? "Cuisine" : "Shop type";

  /* ---------------- effects ---------------- */

  // Start the map somewhere useful when the browser will say where it
  // is; the Toronto default is a poor guess for most of the country.
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (position) =>
        setMarkerPosition({ lat: position.coords.latitude, lng: position.coords.longitude }),
      () => {}
    );
  }, []);

  useEffect(() => {
    if (rootCategoryId || !rootCategories.length) return;
    setRootCategoryId(rootCategories[0]._id);
    setRootCategoryTitle(rootCategories[0].title);
  }, [rootCategories, rootCategoryId]);

  useEffect(() => {
    if (!isSuperAdmin) return undefined;
    const handler = setTimeout(() => {
      getShopAdmins({ variables: { type: "SHOP-ADMIN", fullName: adminSearch.trim() } });
    }, 300);
    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminSearch, isSuperAdmin]);

  // Creating a shop changes what this admin is allowed to see, and the
  // token in hand predates it — so it is swapped before moving on.
  useEffect(() => {
    if (!tokenData) return;
    login(
      tokenData.getNewAccessTokenByAdmin.token.accessToken.toString(),
      tokenData.getNewAccessTokenByAdmin.token.refreshToken.toString()
    );
    window.location.href = endPoint + "/api/v1/paymentMethod";
  }, [tokenData]);

  /* ---------------- unsaved changes ---------------- */

  const hasChanges =
    Boolean(
      name ||
        description ||
        phoneNumbers.some(Boolean) ||
        streetAddress ||
        town ||
        province ||
        postalCode ||
        bannerImage ||
        logoImage ||
        preparingTime
    ) && !submitted;

  useEffect(() => {
    window.onbeforeunload = hasChanges ? () => true : undefined;
    return () => {
      window.onbeforeunload = null;
    };
  }, [hasChanges]);

  /* ---------------- handlers ---------------- */

  const setPhoneNumber = (index, value) =>
    setPhoneNumbers((current) => current.map((item, i) => (i === index ? value : item)));

  const toggleCategory = (id) =>
    setCategoryIds((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
    );

  const toggleDay = (day) =>
    setWorkingHours((current) => {
      const next = Object.assign({}, current);
      if (next[day]) delete next[day];
      else next[day] = Object.assign({}, DEFAULT_HOURS);
      return next;
    });

  const setDayTime = (day, edge, value) =>
    setWorkingHours((current) =>
      Object.assign({}, current, { [day]: Object.assign({}, current[day], { [edge]: value }) })
    );

  const firstOpenDay = WEEK_DAYS.find((day) => workingHours[day.value]);

  const applyFirstDayToAll = () => {
    if (!firstOpenDay) return;
    const hours = workingHours[firstOpenDay.value];
    const next = {};
    Object.keys(workingHours).forEach((day) => {
      next[day] = Object.assign({}, hours);
    });
    setWorkingHours(next);
  };

  const uploadImage = (file, kind) => {
    const setUploading = kind === "banner" ? setUploadingBanner : setUploadingLogo;
    const setImage = kind === "banner" ? setBannerImage : setLogoImage;

    setUploading(true);
    uploadFile({ variables: { file: file.src.file, folderName: `${kind}Image` } })
      .then((res) => {
        setUploading(false);
        setImage(res.data.uploadFile.url);
      })
      .catch((error) => {
        // The old version had no catch at all, so a rejected upload
        // left the spinner turning for good.
        setUploading(false);
        setFormError(readableError(error));
      });
  };

  const validate = () => {
    const errors = {};

    if (!name.trim()) errors.name = "Enter a shop name";
    if (isSuperAdmin && !shopAdminId) errors.shopAdmin = "Choose who owns this shop";
    if (!description.trim()) errors.description = "Add a short description";
    if (!phoneNumbers[0].trim()) errors.phone1 = "Enter a phone number";

    if (!String(preparingTime).trim()) errors.preparingTime = "Enter a preparing time";
    else if (!/^([1-9]|[1-5]\d|60)$/.test(String(preparingTime).trim()) || Number(preparingTime) < 5)
      errors.preparingTime = "Enter a number between 5 and 60";

    if (!String(minimumPurchase).trim())
      errors.minimumPurchase = "Enter an amount, or 0 if you do not offer this";

    if (!streetAddress.trim()) errors.streetAddress = "Enter the street address";
    if (!town.trim()) errors.town = "Enter the town or city";
    if (!province.trim()) errors.province = "Enter the province";
    if (!postalCode.trim()) errors.postalCode = "Enter the postal code";

    if (smsEnabled && !smsPhone.trim())
      errors.smsPhone = "Enter the number the alerts should go to";

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const buildVariables = () => ({
    name: name.trim(),
    description: description.trim(),
    origin: town.trim(),
    address: `${postalCode.trim()} ${streetAddress.trim()} ${town.trim()} ${province.trim()}`,
    preparingTime: parseInt(preparingTime, 10),
    minimumPurchase: parseInt(minimumPurchase, 10),
    newOrderSMSNotificationPhoneNumber: smsPhone,
    newOrderSMSNotificationEnabled: smsEnabled,
    phoneNumbers: phoneNumbers.map((item) => item.trim()).filter(Boolean),
    location: {
      coordinates: [parseFloat(markerPosition.lng), parseFloat(markerPosition.lat)],
      type: "Point",
    },
    budget,
    bannerUrl: bannerImage,
    logoUrl: logoImage,
    rootCategory: rootCategoryId,
    categories: categoryIds,
    notWorkingDays: WEEK_DAYS.filter((day) => !workingHours[day.value]).map((day) => ({
      type: day.value,
    })),
    workingHoursInMinutes: Object.keys(workingHours).map((day) => ({
      type: day.toUpperCase(),
      from: timeToMinutes(workingHours[day].from),
      to: timeToMinutes(workingHours[day].to),
    })),
  });

  const handleSubmit = () => {
    if (saving) return;
    setFormError("");
    if (!validate()) {
      setFormError("Some details are missing. Check the highlighted fields.");
      return;
    }

    setSaving(true);
    setSubmitted(true);
    window.onbeforeunload = null;

    const variables = buildVariables();

    if (isSuperAdmin) {
      createShopByAdmin({ variables: Object.assign({ shopAdminId: shopAdminId.trim() }, variables) })
        .then(() => {
          window.location.href = endPoint + "/superAdmin/shops";
        })
        .catch((error) => {
          setSaving(false);
          setSubmitted(false);
          setFormError(readableError(error));
        });
      return;
    }

    createShopByShopAdmin({ variables: Object.assign({ active: true }, variables) })
      .then((res) => {
        const shop = res.data.createShopByShopAdmin;
        window.localStorage.setItem("shopId", String(shop._id));
        if (shop.rootCategory && shop.rootCategory.title) {
          window.localStorage.setItem("shopType", String(shop.rootCategory.title));
        }
        getNewAccessToken({
          variables: { refreshToken: window.localStorage.getItem("refreshToken") },
        });
      })
      .catch((error) => {
        setSaving(false);
        setSubmitted(false);
        setFormError(readableError(error));
      });
  };

  /* ---------------- render ---------------- */

  if (rootLoading) {
    return (
      <div className="addShopForm">
        <Skeleton height={220} />
        <Skeleton height={320} />
      </div>
    );
  }

  const formBody = (
    <div className="addShopForm">
      <Prompt
        when={hasChanges}
        message="You have unsaved changes. Are you sure you want to leave?"
      />

      {formError && (
        <div className="dsFormMessage error" role="alert">
          <ErrorOutlineIcon />
          <span>{formError}</span>
        </div>
      )}

      {isSuperAdmin && (
        <Panel title="Owner" subtitle="Which shop admin this shop belongs to.">
          <Field label="Shop admin" required error={fieldErrors.shopAdmin}>
            <div className="addShopAdminPicker">
              <SearchField
                placeholder="Search by name"
                value={adminSearch}
                onChange={(event) => setAdminSearch(event.target.value)}
              />
              <div className="addShopAdminList">
                {adminsLoading ? (
                  <div className="addShopAdminEmpty">Searching</div>
                ) : shopAdmins.length === 0 ? (
                  <div className="addShopAdminEmpty">
                    No shop admin without a shop matches that.
                  </div>
                ) : (
                  shopAdmins.map((admin) => (
                    <button
                      key={admin._id}
                      type="button"
                      className={`addShopAdmin ${shopAdminId === admin._id ? "isSelected" : ""}`}
                      onClick={() => setShopAdminId(admin._id)}
                    >
                      <span className="addShopAdminBody">
                        <strong>{admin.fullName}</strong>
                        <span>{admin.phoneNumber}</span>
                      </span>
                      <span className="addShopAdminCheck">
                        <CheckCircleIcon />
                      </span>
                    </button>
                  ))
                )}
              </div>
              {selectedAdmin && (
                <span className="dsFieldHint">
                  This shop will belong to {selectedAdmin.fullName}.
                </span>
              )}
            </div>
          </Field>
        </Panel>
      )}

      <Panel title="Shop profile" subtitle="How the shop appears to customers.">
        <div className="addShopPreview">
          <div className="addShopPreviewBanner">
            {bannerImage ? (
              <img src={`${graphqlUrl}${bannerImage}`} alt="Shop banner" />
            ) : (
              <StorefrontIcon fontSize="large" />
            )}
          </div>
          <div className="addShopPreviewRow">
            <div className="addShopPreviewLogo">
              {logoImage ? (
                <img src={`${graphqlUrl}${logoImage}`} alt="Shop logo" />
              ) : (
                <StorefrontIcon />
              )}
            </div>
            <div className="addShopPreviewText">
              <h3>{name || "Your shop name"}</h3>
              <p>{description || "A short description of your shop"}</p>
            </div>
          </div>
        </div>

        <div className="addShopGrid">
          <Field label="Shop name" required error={fieldErrors.name}>
            <input
              className="dsInput"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </Field>
          <Field label="Price range">
            <div className="addShopBudgetRow">
              {BUDGET_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  title={option.hint}
                  className={`addShopBudgetOption ${budget === option.value ? "isSelected" : ""}`}
                  onClick={() => setBudget(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </Field>
        </div>

        <Field
          label="Description"
          required
          error={fieldErrors.description}
          hint="Shown on the shop's page."
        >
          <textarea
            className="dsInput addShopTextarea"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </Field>

        <Field label="Shop type" hint="Decides which categories and products are available.">
          <div className="addShopOptionRow">
            {rootCategories.map((category) => (
              <label
                key={category._id}
                className={`dsOptionCard ${rootCategoryId === category._id ? "isSelected" : ""}`}
              >
                <input
                  type="radio"
                  checked={rootCategoryId === category._id}
                  onChange={() => {
                    setRootCategoryId(category._id);
                    setRootCategoryTitle(category.title);
                    setCategoryIds([]);
                  }}
                />
                <span className="dsOptionCardText">
                  <strong>{category.title === "Restaurant" ? "Restaurant" : "Shop"}</strong>
                </span>
              </label>
            ))}
          </div>
        </Field>

        {subCategories.length > 0 && (
          <Field label={categoryLabel}>
            <div className="addShopOptionRow">
              {subCategories.map((category) => (
                <label
                  key={category._id}
                  className={`dsOptionCard ${
                    categoryIds.includes(category._id) ? "isSelected" : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={categoryIds.includes(category._id)}
                    onChange={() => toggleCategory(category._id)}
                  />
                  <span className="dsOptionCardText">{category.title}</span>
                </label>
              ))}
            </div>
          </Field>
        )}

        <div className="addShopGrid">
          <Field label="Banner image" hint="Sits at the top of the shop's page. Under 2MB.">
            <Files
              maxSize="2mb"
              multiple={false}
              accept={["image/jpg", "image/jpeg", "image/png"]}
              onSuccess={(files) => uploadImage(files[0], "banner")}
            >
              {({ browseFiles, getDropZoneProps }) => (
                <div {...getDropZoneProps({ className: "addShopImageUpload" })}>
                  {uploadingBanner ? (
                    <CircularProgress size={22} />
                  ) : bannerImage ? (
                    <img src={`${graphqlUrl}${bannerImage}`} alt="Banner preview" />
                  ) : (
                    <StorefrontIcon />
                  )}
                  <Button size="sm" type="button" onClick={browseFiles}>
                    {bannerImage ? "Change image" : "Select image"}
                  </Button>
                </div>
              )}
            </Files>
          </Field>
          <Field label="Logo image" hint="Sits next to the shop's name. Under 2MB.">
            <Files
              maxSize="2mb"
              multiple={false}
              accept={["image/jpg", "image/jpeg", "image/png"]}
              onSuccess={(files) => uploadImage(files[0], "logo")}
            >
              {({ browseFiles, getDropZoneProps }) => (
                <div {...getDropZoneProps({ className: "addShopImageUpload" })}>
                  {uploadingLogo ? (
                    <CircularProgress size={22} />
                  ) : logoImage ? (
                    <img src={`${graphqlUrl}${logoImage}`} alt="Logo preview" />
                  ) : (
                    <StorefrontIcon />
                  )}
                  <Button size="sm" type="button" onClick={browseFiles}>
                    {logoImage ? "Change image" : "Select image"}
                  </Button>
                </div>
              )}
            </Files>
          </Field>
        </div>
      </Panel>

      <Panel
        title="Contact & location"
        subtitle="How customers and drivers reach the shop."
      >
        <div className="addShopGrid">
          <Field label="Phone number" required error={fieldErrors.phone1}>
            <input
              className="dsInput"
              value={phoneNumbers[0]}
              onChange={(event) => setPhoneNumber(0, event.target.value)}
            />
          </Field>
          <Field label="Phone number" optional>
            <input
              className="dsInput"
              value={phoneNumbers[1]}
              onChange={(event) => setPhoneNumber(1, event.target.value)}
            />
          </Field>
          <Field label="Phone number" optional>
            <input
              className="dsInput"
              value={phoneNumbers[2]}
              onChange={(event) => setPhoneNumber(2, event.target.value)}
            />
          </Field>
        </div>

        <div className="addShopGrid">
          <Field label="Street address" required error={fieldErrors.streetAddress}>
            <input
              className="dsInput"
              value={streetAddress}
              onChange={(event) => setStreetAddress(event.target.value)}
            />
          </Field>
          <Field label="Town / city" required error={fieldErrors.town}>
            <input
              className="dsInput"
              value={town}
              onChange={(event) => setTown(event.target.value)}
            />
          </Field>
          <Field label="Province" required error={fieldErrors.province}>
            <input
              className="dsInput"
              value={province}
              onChange={(event) => setProvince(event.target.value)}
            />
          </Field>
          <Field label="Postal code" required error={fieldErrors.postalCode}>
            <input
              className="dsInput"
              value={postalCode}
              onChange={(event) => setPostalCode(event.target.value)}
            />
          </Field>
        </div>

        <Field
          label="Pin on the map"
          hint="Search or click the map — the address fields above fill in from it. Drivers are sent to this pin."
        >
          <div className="addShopMap">
            <FunMap
              position={markerPosition}
              city={town}
              setCity={setTown}
              setStreet={setStreetAddress}
              setProvince={setProvince}
              setPostalCode={setPostalCode}
              setAddress={() => {}}
              onSetmarkerPosition={(lat, lng) => setMarkerPosition({ lat, lng })}
            />
          </div>
        </Field>
      </Panel>

      <Panel
        title="Opening hours"
        subtitle="Days left off are treated as closed all day."
        actions={
          firstOpenDay && (
            <Button size="sm" type="button" onClick={applyFirstDayToAll}>
              Copy {firstOpenDay.label}'s hours to every open day
            </Button>
          )
        }
      >
        <div className="addShopDayList">
          {WEEK_DAYS.map((day) => {
            const isOpen = Boolean(workingHours[day.value]);
            return (
              <div key={day.value} className={`addShopDayRow ${isOpen ? "isOpen" : ""}`}>
                <label className="addShopDayToggle">
                  <input
                    type="checkbox"
                    checked={isOpen}
                    onChange={() => toggleDay(day.value)}
                  />
                  {day.label}
                </label>
                {isOpen ? (
                  <div className="addShopDayTimes">
                    <input
                      type="time"
                      className="dsInput"
                      value={workingHours[day.value].from}
                      onChange={(event) => setDayTime(day.value, "from", event.target.value)}
                    />
                    <span>to</span>
                    <input
                      type="time"
                      className="dsInput"
                      value={workingHours[day.value].to}
                      onChange={(event) => setDayTime(day.value, "to", event.target.value)}
                    />
                  </div>
                ) : (
                  <span className="addShopDayClosed">Closed</span>
                )}
              </div>
            );
          })}
        </div>
      </Panel>

      <Panel title="Delivery & notifications">
        <div className="addShopGrid">
          <Field
            label="Order preparing time"
            required
            error={fieldErrors.preparingTime}
            hint="Minutes, between 5 and 60."
          >
            <input
              type="number"
              className="dsInput"
              value={preparingTime}
              onChange={(event) => setPreparingTime(event.target.value)}
            />
          </Field>
          <Field
            label="Free delivery minimum"
            required
            error={fieldErrors.minimumPurchase}
            hint="Order total that unlocks free delivery. Use 0 to turn this off."
          >
            <input
              type="number"
              className="dsInput"
              value={minimumPurchase}
              onChange={(event) => setMinimumPurchase(event.target.value)}
            />
          </Field>
        </div>

        <Field label="SMS about new orders">
          <label className="addShopSwitchRow">
            <Switch
              checked={smsEnabled}
              onChange={(event) => setSmsEnabled(event.target.checked)}
              color="primary"
            />
            <span>{smsEnabled ? "Enabled" : "Disabled"}</span>
          </label>
        </Field>
        {smsEnabled && (
          <Field label="Notification phone number" required error={fieldErrors.smsPhone}>
            <input
              className="dsInput"
              value={smsPhone}
              onChange={(event) => setSmsPhone(event.target.value)}
            />
          </Field>
        )}
      </Panel>

      <div className="addShopActionBar">
        {isSuperAdmin && (
          <Button
            type="button"
            variant="secondary"
            disabled={saving}
            onClick={() => {
              window.onbeforeunload = null;
              window.location.href = endPoint + "/superAdmin/shops";
            }}
          >
            Cancel
          </Button>
        )}
        <Button variant="primary" type="button" loading={saving} onClick={handleSubmit}>
          {isSuperAdmin ? "Create shop" : "Save and continue"}
        </Button>
      </div>
    </div>
  );

  if (!isSuperAdmin) return formBody;

  return (
    <div className="page addShopPage">
      <main className="main">
        <PageHeader
          title="Add a shop"
          description="Create a shop on behalf of a shop admin who does not have one yet."
        />
        {formBody}
      </main>
    </div>
  );
};

export default AddShop;
